import { AnimatedContentLoader, Button } from '@root/components'
import { motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'
import NojaForm from './NojaForm'
import { useHistory } from 'react-router-dom'
import { useBusinessContext } from '@root/context'
import { INojaFormProps, sendNojaForm, useCurrentUser } from '@root/query'
import nojaRahoitusImgSrc from '@assets/noja/noja-rahoitus-logo.png'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getUserDisplayName } from '@root/utils'

interface Props {
  onSuccess: () => void
}

const NojaSendForm: React.FC<Props> = ({ onSuccess }) => {
  const history = useHistory()
  const [t] = useTranslation()
  const {
    slug,
    data: { name: businessName }
  } = useBusinessContext()

  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const userFullName = getUserDisplayName(user, { fallbackToEmail: false })

  const formDefaultData = () => {
    return {
      business_name: businessName,
      business_id: slug,
      contact_name: userFullName,
      contact_email: user?.email
    }
  }

  const sendFormMutation = useMutation({
    mutationFn: (data: INojaFormProps) => sendNojaForm({ slug: slug }, data),
    ...{
      onSuccess: () => {
        onSuccess()
      },
      onError: () => {
        toast.error(t('general.error'))
      }
    }
  })

  return (
    <StyledPageContent>
      <LogoContainer>
        <StyledImg src={nojaRahoitusImgSrc} alt="Noja Rahoitus" />
      </LogoContainer>

      <StyledText>{t('nojaPage.sendForm.description')}</StyledText>

      <AnimatedContentLoader isLoading={isCurrentUserLoading}>
        <NojaForm defaultFormValues={formDefaultData()} sendFormMutation={sendFormMutation} />
      </AnimatedContentLoader>

      <Footer>
        <Button onClick={() => history.goBack()}>{t('nojaPage.sendForm.buttonCancel')}</Button>
        <Button
          intent="success"
          type="submit"
          form="noja-form"
          showSpinner={sendFormMutation.isPending}
        >
          {t('nojaPage.sendForm.buttonSend')}
        </Button>
      </Footer>
    </StyledPageContent>
  )
}

export default NojaSendForm

const LogoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 3.2rem; // Using the logo's letter heigth (64%) in the logo to determine the padding
  box-sizing: border-box;
`

const StyledImg = styled.img`
  width: auto;
  height: 5rem;
`

const StyledText = styled.p`
  text-align: center;
`

const StyledPageContent = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 }
})`
  position: relative;
  display: flex;

  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 1000px;
  max-width: 600px;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  overflow: auto;
  box-shadow: 0px 5px 10px 1px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 100%;
  }
`
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
`
